import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Save, CheckCircle, XCircle, HelpCircle, RefreshCw, Plus, Copy, Key, Wifi, WifiOff, Terminal, AlertCircle, Users, Download } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';

type TabType = 'dashboard' | 'general' | 'wireguard' | 'mumble-bots';

interface StatusCheck {
  status: 'ok' | 'error' | 'not_set' | 'not_found' | 'no_access' | 'permission_error' | 'unknown' | 'checking';
  message: string;
  value?: string;
  resolved_ip?: string;
  record_value?: string;
}

interface ServerStatus {
  ipv4: StatusCheck;
  dns_record: StatusCheck;
  dns_resolution: StatusCheck;
  ssh: StatusCheck;
  mumble_installed: StatusCheck;
  mumble: StatusCheck;
}

interface WireGuardStatus {
  configured: boolean;
  enabled: boolean;
  wg_ip: string | null;
  public_key: string | null;
  connected: boolean;
  last_handshake: string | null;
  transfer_rx: number | null;
  transfer_tx: number | null;
}

export default function MumbleServerEdit() {
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const [activeTab, setActiveTab] = useState<TabType>(isNew ? 'general' : 'dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    ipv4: '',
    host: '',
    port: 64738,
    ice_host: '',
    ice_port: 6502,
    ice_secret: '',
    max_users: 100,
    max_bandwidth: 72000,
    is_active: true,
    is_default: false,
  });

  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [creatingDns, setCreatingDns] = useState(false);

  // WireGuard state
  const [wgStatus, setWgStatus] = useState<WireGuardStatus | null>(null);
  const [wgLoading, setWgLoading] = useState(false);
  const [wgConfig, setWgConfig] = useState<string | null>(null);
  const [wgInstallScript, setWgInstallScript] = useState<string | null>(null);
  const [wgGenerating, setWgGenerating] = useState(false);
  const [wgEnabling, setWgEnabling] = useState(false);
  const [wgInstalling, setWgInstalling] = useState(false);
  const [wgTerminalOutput, setWgTerminalOutput] = useState<string[]>([]);
  const [wgTerminalStatus, setWgTerminalStatus] = useState<'running' | 'success' | 'error' | 'idle'>('idle');
  const wgTerminalRef = useRef<HTMLDivElement>(null);
  const wgEventSourceRef = useRef<EventSource | null>(null);

  // CopyFlash state (flash-copy pattern)
  const [copyFlash, setCopyFlash] = useState(false);
  const [copyToast, setCopyToast] = useState(false);

  // Mumble installation state
  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState<{success: boolean; message: string; output?: string} | null>(null);

  // Terminal modal state
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState<string[]>([]);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    tenants_synced: number;
    total_channels: number;
    synced_channels: number;
    failed_channels: number;
    total_users_synced: number;
    total_audiocast_synced: number;
    errors: string[];
  } | null>(null);
  const [terminalStatus, setTerminalStatus] = useState<'running' | 'success' | 'error' | 'idle'>('idle');
  const terminalRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // ICE secret fetch state
  const [fetchingIceSecret, setFetchingIceSecret] = useState(false);

  // AudioCast Bot state
  const [audiocastBotStatus, setAudiocastBotStatus] = useState<{
    installed: boolean;
    active: boolean;
    message: string;
  } | null>(null);
  const [audiocastBotInstalling, setAudiocastBotInstalling] = useState(false);
  const [audiocastBotTerminalOutput, setAudiocastBotTerminalOutput] = useState<string[]>([]);
  const [audiocastBotTerminalStatus, setAudiocastBotTerminalStatus] = useState<'running' | 'success' | 'error' | 'idle'>('idle');
  const audiocastBotEventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    loadData();
    // When navigating from new to existing server, switch to dashboard
    if (id) {
      setActiveTab('dashboard');
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'dashboard') {
      loadStatus();
      loadWgStatus();  // Also load WireGuard status for dashboard
      loadAudiocastBotStatus();  // Also load AudioCast bot status
    }
    if (id && activeTab === 'wireguard') {
      loadWgStatus();
    }
  }, [id, activeTab]);

  // Auto-retry DNS resolution when DNS record is ok but resolution failed
  useEffect(() => {
    if (
      serverStatus?.dns_record.status === 'ok' &&
      serverStatus?.dns_resolution.status === 'error' &&
      activeTab === 'dashboard' &&
      !statusLoading
    ) {
      const timer = setTimeout(() => {
        loadStatus();
      }, 5000); // Retry after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [serverStatus, activeTab, statusLoading]);

  // Helper function for authenticated fetch
  const authFetch = (url: string, options: RequestInit = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${keycloak?.token}`
      }
    });
  };

  const loadData = async () => {
    setLoading(true);

    if (id) {
      // Load existing server
      const res = await authFetch('/api/mumble-servers');
      if (res.ok) {
        const servers = await res.json();
        const server = servers.find((s: any) => s.id === id);
        if (server) {
          setForm({
            name: server.name,
            ipv4: server.ipv4 || '',
            host: server.host,
            port: server.port,
            ice_host: server.ice_host || '',
            ice_port: server.ice_port || 6502,
            ice_secret: server.ice_secret || '',
            max_users: server.max_users,
            max_bandwidth: server.max_bandwidth,
            is_active: server.is_active,
            is_default: server.is_default,
          });
        }
      }
    } else {
      // Load defaults for new server
      const res = await authFetch('/api/mumble-servers/defaults');
      if (res.ok) {
        const defaults = await res.json();
        setForm({
          name: defaults.name,
          ipv4: '',
          host: defaults.host,
          port: defaults.port,
          ice_host: defaults.ice_host,
          ice_port: defaults.ice_port,
          ice_secret: defaults.ice_secret,
          max_users: defaults.max_users,
          max_bandwidth: defaults.max_bandwidth,
          is_active: defaults.is_active,
          is_default: defaults.is_default,
        });
      }
    }

    setLoading(false);
  };

  const loadStatus = async () => {
    if (!id) return;
    setStatusLoading(true);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/status`);
      if (res.ok) {
        setServerStatus(await res.json());
      }
    } catch (error) {
      console.error('Failed to load status:', error);
    }
    setStatusLoading(false);
  };

  const syncTenants = async () => {
    if (!id) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/sync-tenants`, { method: 'POST' });
      if (res.ok) {
        const result = await res.json();
        setSyncResult(result);
      } else {
        const data = await res.json();
        alert(data.detail || 'Fehler bei der Synchronisierung');
      }
    } catch (error) {
      console.error('Failed to sync tenants:', error);
      alert('Fehler bei der Synchronisierung');
    }
    setSyncing(false);
  };

  const createDnsRecord = async () => {
    if (!id) return;
    setCreatingDns(true);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/dns-record`, { method: 'POST' });
      if (res.ok) {
        await loadStatus();
      } else {
        const data = await res.json();
        alert(data.detail || 'Fehler beim Erstellen des DNS-Eintrags');
      }
    } catch (error) {
      console.error('Failed to create DNS record:', error);
      alert('Fehler beim Erstellen des DNS-Eintrags');
    }
    setCreatingDns(false);
  };

  // ICE Secret fetch function
  const fetchIceSecret = async () => {
    if (!id) return;
    setFetchingIceSecret(true);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/fetch-ice-secret`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        // Reload server data to get updated ice_secret
        await loadData();
        alert(data.message);
      } else {
        const data = await res.json();
        alert(data.detail || 'Fehler beim Abrufen des ICE Secrets');
      }
    } catch (error) {
      console.error('Failed to fetch ICE secret:', error);
      alert('Fehler beim Abrufen des ICE Secrets');
    }
    setFetchingIceSecret(false);
  };

  // AudioCast Bot functions
  const loadAudiocastBotStatus = async () => {
    if (!id) return;
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/audiocast-bot/status`);
      if (res.ok) {
        setAudiocastBotStatus(await res.json());
      }
    } catch (error) {
      console.error('Failed to load AudioCast bot status:', error);
    }
  };

  const installAudiocastBot = () => {
    if (!id) return;

    // Close existing connection if any
    if (audiocastBotEventSourceRef.current) {
      audiocastBotEventSourceRef.current.close();
    }

    setAudiocastBotInstalling(true);
    setAudiocastBotTerminalOutput([]);
    setAudiocastBotTerminalStatus('running');
    setShowTerminal(true);
    setTerminalOutput([]);
    setTerminalStatus('running');

    const eventSource = new EventSource(`/api/mumble-servers/${id}/audiocast-bot/install/stream`);
    audiocastBotEventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'start':
            setTerminalOutput(prev => [...prev, `>>> ${data.message}`]);
            break;
          case 'output':
            setTerminalOutput(prev => [...prev, data.line]);
            break;
          case 'success':
            setTerminalOutput(prev => [...prev, '', `✓ ${data.message}`]);
            setTerminalStatus('success');
            setAudiocastBotInstalling(false);
            loadAudiocastBotStatus();
            eventSource.close();
            break;
          case 'error':
            setTerminalOutput(prev => [...prev, '', `✗ ${data.message}`]);
            setTerminalStatus('error');
            setAudiocastBotInstalling(false);
            eventSource.close();
            break;
          case 'done':
            eventSource.close();
            break;
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e);
      }
    };

    eventSource.onerror = () => {
      setTerminalOutput(prev => [...prev, '', '✗ Verbindung zum Server verloren']);
      setTerminalStatus('error');
      setAudiocastBotInstalling(false);
      eventSource.close();
    };
  };

  // WireGuard functions
  const loadWgStatus = async () => {
    if (!id) return;
    setWgLoading(true);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/wireguard/status`);
      if (res.ok) {
        const status = await res.json();
        setWgStatus(status);
        // Also load config if configured
        if (status.configured) {
          loadWgConfig();
        }
      }
    } catch (error) {
      console.error('Failed to load WireGuard status:', error);
    }
    setWgLoading(false);
  };

  const loadWgConfig = async () => {
    if (!id) return;
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/wireguard/client-config`);
      if (res.ok) {
        const data = await res.json();
        setWgConfig(data.config);
        setWgInstallScript(data.install_script);
      }
    } catch (error) {
      console.error('Failed to load WireGuard config:', error);
    }
  };

  const generateWgKeys = async () => {
    if (!id) return;
    setWgGenerating(true);
    try {
      const res = await authFetch(`/api/mumble-servers/${id}/wireguard/generate-keys`, { method: 'POST' });
      if (res.ok) {
        await loadWgStatus();
      } else {
        const data = await res.json();
        alert(data.detail || 'Fehler beim Generieren der Keys');
      }
    } catch (error) {
      console.error('Failed to generate WireGuard keys:', error);
      alert('Fehler beim Generieren der Keys');
    }
    setWgGenerating(false);
  };

  const toggleWgEnabled = async () => {
    if (!id || !wgStatus) return;
    setWgEnabling(true);
    try {
      const action = wgStatus.enabled ? 'disable' : 'enable';
      const res = await authFetch(`/api/mumble-servers/${id}/wireguard/${action}`, { method: 'POST' });
      if (res.ok) {
        await loadWgStatus();
      } else {
        const data = await res.json();
        alert(data.detail || `Fehler beim ${action === 'enable' ? 'Aktivieren' : 'Deaktivieren'}`);
      }
    } catch (error) {
      console.error('Failed to toggle WireGuard:', error);
    }
    setWgEnabling(false);
  };

  // Install WireGuard on target server with SSE streaming
  const installWireGuard = () => {
    if (!id) return;

    // Close existing connection if any
    if (wgEventSourceRef.current) {
      wgEventSourceRef.current.close();
    }

    setWgInstalling(true);
    setWgTerminalOutput([]);
    setWgTerminalStatus('running');

    const eventSource = new EventSource(`/api/mumble-servers/${id}/wireguard/install/stream`);
    wgEventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'start':
            setWgTerminalOutput([data.message, '']);
            break;
          case 'output':
            setWgTerminalOutput(prev => [...prev, data.line]);
            // Auto-scroll
            setTimeout(() => {
              if (wgTerminalRef.current) {
                wgTerminalRef.current.scrollTop = wgTerminalRef.current.scrollHeight;
              }
            }, 10);
            break;
          case 'success':
            setWgTerminalOutput(prev => [...prev, '', `✓ ${data.message}`]);
            setWgTerminalStatus('success');
            setWgInstalling(false);
            loadWgStatus();
            eventSource.close();
            break;
          case 'error':
            setWgTerminalOutput(prev => [...prev, '', `✗ ${data.message}`]);
            setWgTerminalStatus('error');
            setWgInstalling(false);
            eventSource.close();
            break;
          case 'done':
            eventSource.close();
            break;
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e);
      }
    };

    eventSource.onerror = () => {
      setWgTerminalOutput(prev => [...prev, '', '✗ Verbindung zum Server verloren']);
      setWgTerminalStatus('error');
      setWgInstalling(false);
      eventSource.close();
    };
  };

  // CopyFlash: Button-Flash + Toast-Popup Pattern
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Flash button blue for 500ms
    setCopyFlash(true);
    setTimeout(() => setCopyFlash(false), 500);
    // Show toast for 1000ms
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 1000);
  };

  // Install Mumble on target server with SSE streaming
  const installMumble = () => {
    if (!id) return;

    // Reset state and open modal
    setTerminalOutput([]);
    setTerminalStatus('running');
    setShowTerminal(true);
    setInstalling(true);
    setInstallResult(null);

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create SSE connection
    const eventSource = new EventSource(`/api/mumble-servers/${id}/install-mumble/stream`);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'start':
            setTerminalOutput(prev => [...prev, `>>> ${data.message}`]);
            break;
          case 'output':
            setTerminalOutput(prev => [...prev, data.line]);
            break;
          case 'success':
            setTerminalOutput(prev => [...prev, '', `✓ ${data.message}`]);
            setTerminalStatus('success');
            setInstallResult({ success: true, message: data.message });
            setInstalling(false);
            eventSource.close();
            // Reload status after successful installation
            loadStatus();
            loadWgStatus();
            break;
          case 'error':
            setTerminalOutput(prev => [...prev, '', `✗ ${data.message}`]);
            setTerminalStatus('error');
            setInstallResult({ success: false, message: data.message });
            setInstalling(false);
            eventSource.close();
            break;
          case 'done':
            eventSource.close();
            break;
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e);
      }
    };

    eventSource.onerror = () => {
      setTerminalOutput(prev => [...prev, '', '✗ Verbindung zum Server unterbrochen']);
      setTerminalStatus('error');
      setInstallResult({ success: false, message: 'Verbindung unterbrochen' });
      setInstalling(false);
      eventSource.close();
    };
  };

  // Auto-scroll terminal to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (audiocastBotEventSourceRef.current) {
        audiocastBotEventSourceRef.current.close();
      }
    };
  }, []);

  const closeTerminal = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    setShowTerminal(false);
    setInstalling(false);
  };

  const handleSubmit = async () => {
    // Validate IPv4
    if (form.ipv4 && (form.ipv4.length > 15 || !/^[\d.]+$/.test(form.ipv4))) {
      alert('IPv4 darf nur Ziffern und Punkte enthalten (max. 15 Zeichen)');
      return;
    }

    setSaving(true);

    const payload = {
      ...form,
      ipv4: form.ipv4 || null,
      ice_host: form.ice_host || null,
      ice_port: form.ice_host ? form.ice_port : null,
      ice_secret: form.ice_secret || null,
    };

    try {
      if (id) {
        // Editing existing server
        await authFetch(`/api/mumble-servers/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        navigate('/mumble-servers');
      } else {
        // Creating new server - navigate to its dashboard after creation
        const res = await authFetch('/api/mumble-servers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const newServer = await res.json();
          navigate(`/mumble-servers/${newServer.id}/edit`);
        } else {
          throw new Error('Failed to create server');
        }
      }
    } catch (error) {
      console.error('Failed to save server:', error);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/mumble-servers');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {isNew ? 'Neuer Mumble Server' : `Mumble Server: ${form.name}`}
          </h1>
          {isNew && (
            <p className="text-slate-500 mt-1">
              Erstellen Sie einen neuen Mumble Server
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg transition-colors"
          >
            <X size={18} />
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.name || !form.host || !form.ipv4}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              form.name && form.host && form.ipv4
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-300 text-white cursor-not-allowed'
            }`}
          >
            <Save size={18} />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'dashboard'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Allgemeines
          </button>
          <button
            onClick={() => setActiveTab('wireguard')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'wireguard'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            WireGuard
          </button>
          <button
            onClick={() => setActiveTab('mumble-bots')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'mumble-bots'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Mumble-Bots
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="p-6">
            {isNew ? (
              <div className="text-center text-slate-500 py-12">
                Speichern Sie den Server zuerst, um den Status zu sehen.
              </div>
            ) : (
              <div className="space-y-4 max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-slate-700">Server Status</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={syncTenants}
                      disabled={syncing}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      title="Alle Tenants auf diesem Server synchronisieren"
                    >
                      <Users size={16} className={syncing ? 'animate-pulse' : ''} />
                      {syncing ? 'Synchronisiere...' : 'Tenants synchronisieren'}
                    </button>
                    <button
                      onClick={() => { loadStatus(); loadWgStatus(); loadAudiocastBotStatus(); }}
                      disabled={statusLoading || wgLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={16} className={statusLoading || wgLoading ? 'animate-spin' : ''} />
                      Aktualisieren
                    </button>
                  </div>
                </div>

                {statusLoading && !serverStatus ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : serverStatus ? (
                  <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
                    {/* IPv4 Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.ipv4.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {serverStatus.ipv4.status === 'error' && <XCircle size={20} className="text-red-500" />}
                        {serverStatus.ipv4.status === 'not_set' && <XCircle size={20} className="text-red-500" />}
                        {serverStatus.ipv4.status === 'no_access' && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">Server IPv4</div>
                          <div className="text-sm text-slate-500">
                            {serverStatus.ipv4.value || serverStatus.ipv4.message}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SSH Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.ssh.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {serverStatus.ssh.status === 'error' && <XCircle size={20} className="text-red-500" />}
                        {(serverStatus.ssh.status === 'no_access' || serverStatus.ssh.status === 'unknown') && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">SSH Status</div>
                          <div className="text-sm text-slate-500">{serverStatus.ssh.message}</div>
                        </div>
                      </div>
                    </div>

                    {/* DNS Record Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.dns_record.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {(serverStatus.dns_record.status === 'error' || serverStatus.dns_record.status === 'not_found') && <XCircle size={20} className="text-red-500" />}
                        {serverStatus.dns_record.status === 'not_set' && <XCircle size={20} className="text-red-500" />}
                        {(serverStatus.dns_record.status === 'no_access' || serverStatus.dns_record.status === 'permission_error' || serverStatus.dns_record.status === 'unknown') && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">DNS-Eintrag (Cloudflare)</div>
                          <div className="text-sm text-slate-500">{serverStatus.dns_record.message}</div>
                        </div>
                      </div>
                      <button
                        onClick={createDnsRecord}
                        disabled={creatingDns || !form.ipv4}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!form.ipv4 ? 'IPv4-Adresse erforderlich' : 'DNS-Eintrag erstellen oder aktualisieren'}
                      >
                        <Plus size={14} />
                        {creatingDns ? 'Erstelle...' : 'Eintrag neu setzen'}
                      </button>
                    </div>

                    {/* DNS Resolution Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.dns_resolution.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {serverStatus.dns_resolution.status === 'error' && <XCircle size={20} className="text-red-500" />}
                        {(serverStatus.dns_resolution.status === 'no_access' || serverStatus.dns_resolution.status === 'unknown') && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">
                            DNS-Auflösung: {form.host || '(kein Hostname)'}
                          </div>
                          <div className="text-sm text-slate-500">
                            {serverStatus.dns_resolution.resolved_ip
                              ? `${serverStatus.dns_resolution.message} (${serverStatus.dns_resolution.resolved_ip})`
                              : serverStatus.dns_resolution.message}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* WireGuard Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {wgStatus?.connected ? (
                          <CheckCircle size={20} className="text-green-500" />
                        ) : wgStatus?.enabled ? (
                          <HelpCircle size={20} className="text-yellow-500" />
                        ) : wgStatus?.configured ? (
                          <XCircle size={20} className="text-red-500" />
                        ) : (
                          <HelpCircle size={20} className="text-slate-400" />
                        )}
                        <div>
                          <div className="font-medium text-slate-700">WireGuard VPN</div>
                          <div className="text-sm text-slate-500">
                            {wgStatus?.connected ? 'Verbunden' :
                             wgStatus?.enabled ? 'Aktiviert, warte auf Verbindung' :
                             wgStatus?.configured ? 'Konfiguriert, aber deaktiviert' :
                             'Nicht konfiguriert'}
                            {wgStatus?.wg_ip && ` (${wgStatus.wg_ip})`}
                          </div>
                        </div>
                      </div>
                      {!wgStatus?.connected && (
                        <button
                          onClick={() => setActiveTab('wireguard')}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          WireGuard aktivieren →
                        </button>
                      )}
                    </div>

                    {/* Mumble Installation Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.mumble_installed.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {serverStatus.mumble_installed.status === 'error' && <XCircle size={20} className="text-red-500" />}
                        {(serverStatus.mumble_installed.status === 'no_access' || serverStatus.mumble_installed.status === 'unknown') && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">Mumble Installation</div>
                          <div className="text-sm text-slate-500">
                            {installResult ? (
                              <span className={installResult.success ? 'text-green-600' : 'text-red-600'}>
                                {installResult.message}
                              </span>
                            ) : serverStatus.mumble_installed.message}
                          </div>
                        </div>
                      </div>
                      {serverStatus.mumble_installed.status === 'error' && serverStatus.ssh.status === 'ok' && (
                        <button
                          onClick={installMumble}
                          disabled={installing}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {installing ? (
                            <>
                              <RefreshCw size={14} className="animate-spin" />
                              Installiere...
                            </>
                          ) : (
                            <>
                              <Terminal size={14} />
                              Installation ausführen
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Mumble Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {serverStatus.mumble.status === 'ok' && <CheckCircle size={20} className="text-green-500" />}
                        {serverStatus.mumble.status === 'error' && <XCircle size={20} className="text-red-500" />}
                        {(serverStatus.mumble.status === 'no_access' || serverStatus.mumble.status === 'unknown') && <HelpCircle size={20} className="text-yellow-500" />}
                        <div>
                          <div className="font-medium text-slate-700">Mumble Status</div>
                          <div className="text-sm text-slate-500">{serverStatus.mumble.message}</div>
                        </div>
                      </div>
                    </div>

                    {/* ICE Secret Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {form.ice_secret ? (
                          <CheckCircle size={20} className="text-green-500" />
                        ) : (
                          <XCircle size={20} className="text-red-500" />
                        )}
                        <div>
                          <div className="font-medium text-slate-700">ICE Secret</div>
                          <div className="text-sm text-slate-500">
                            {form.ice_secret
                              ? `Konfiguriert (${form.ice_secret.substring(0, 8)}...)`
                              : 'Nicht konfiguriert - Synchronisierung nicht möglich'}
                          </div>
                        </div>
                      </div>
                      {!form.ice_secret && serverStatus.ssh.status === 'ok' && (
                        <button
                          onClick={fetchIceSecret}
                          disabled={fetchingIceSecret}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          <RefreshCw size={14} className={fetchingIceSecret ? 'animate-spin' : ''} />
                          {fetchingIceSecret ? 'Lade...' : 'Secret abrufen'}
                        </button>
                      )}
                    </div>

                    {/* AudioCast-Bot Status */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {audiocastBotStatus?.active ? (
                          <CheckCircle size={20} className="text-green-500" />
                        ) : audiocastBotStatus?.installed ? (
                          <HelpCircle size={20} className="text-yellow-500" />
                        ) : (
                          <XCircle size={20} className="text-red-500" />
                        )}
                        <div>
                          <div className="font-medium text-slate-700">AudioCast-Bot</div>
                          <div className="text-sm text-slate-500">
                            {audiocastBotStatus?.message || 'Status wird geladen...'}
                          </div>
                        </div>
                      </div>
                      {!audiocastBotStatus?.active && serverStatus.ssh.status === 'ok' && (
                        <button
                          onClick={installAudiocastBot}
                          disabled={audiocastBotInstalling}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {audiocastBotInstalling ? (
                            <>
                              <RefreshCw size={14} className="animate-spin" />
                              Installiere...
                            </>
                          ) : (
                            <>
                              <Terminal size={14} />
                              {audiocastBotStatus?.installed ? 'Bot aktivieren' : 'Bot installieren und aktivieren'}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-slate-500 py-12">
                    Klicken Sie auf "Aktualisieren" um den Status zu laden.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="p-6">
            <div className="space-y-6 max-w-2xl">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                  placeholder="z.B. Mumble Server 1"
                  required
                />
              </div>

              {/* IPv4 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">IP v4 *</label>
                <input
                  type="text"
                  value={form.ipv4}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^[\d.]*$/.test(value) && value.length <= 15)) {
                      setForm({ ...form, ipv4: value });
                    }
                  }}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                  placeholder="z.B. 192.168.1.100"
                  maxLength={15}
                />
              </div>

              {/* Host & Port */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Host *</label>
                  <input
                    type="text"
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                    placeholder="mumble.example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Port</label>
                  <input
                    type="number"
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 64738 })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                  />
                </div>
              </div>

              {/* Limits Section */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-sm font-medium text-slate-700 mb-4">Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">Max. Benutzer</label>
                    <input
                      type="number"
                      value={form.max_users}
                      onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value) || 100 })}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">Max. Bandbreite (bit/s)</label>
                    <input
                      type="number"
                      value={form.max_bandwidth}
                      onChange={(e) => setForm({ ...form, max_bandwidth: parseInt(e.target.value) || 72000 })}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-sayses-accent focus:outline-none focus:ring-1 focus:ring-sayses-accent"
                      min={1}
                    />
                  </div>
                </div>
              </div>

              {/* Status Section */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-sm font-medium text-slate-700 mb-4">Status</h3>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-sayses-accent focus:ring-sayses-accent"
                    />
                    <span className="text-slate-600">Aktiv</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_default}
                      onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-sayses-accent focus:ring-sayses-accent"
                    />
                    <span className="text-slate-600">Standard-Server</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WireGuard Tab */}
        {activeTab === 'wireguard' && (
          <div className="p-6">
            {isNew ? (
              <div className="text-center text-slate-500 py-12">
                Speichern Sie den Server zuerst, um WireGuard zu konfigurieren.
              </div>
            ) : wgLoading && !wgStatus ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="space-y-6 max-w-3xl">
                {/* Status Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-slate-700">WireGuard VPN</h3>
                    {wgStatus?.connected ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        <Wifi size={14} /> Verbunden
                      </span>
                    ) : wgStatus?.enabled ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                        <WifiOff size={14} /> Warte auf Verbindung
                      </span>
                    ) : wgStatus?.configured ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                        <WifiOff size={14} /> Deaktiviert
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-sm">
                        Nicht konfiguriert
                      </span>
                    )}
                  </div>
                  <button
                    onClick={loadWgStatus}
                    disabled={wgLoading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={16} className={wgLoading ? 'animate-spin' : ''} />
                    Aktualisieren
                  </button>
                </div>

                {/* Status Details */}
                {wgStatus && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">VPN IP-Adresse:</span>
                        <span className="ml-2 font-mono">{wgStatus.wg_ip || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Public Key:</span>
                        <span className="ml-2 font-mono text-xs">{wgStatus.public_key?.substring(0, 20)}...</span>
                      </div>
                      {wgStatus.last_handshake && (
                        <div>
                          <span className="text-slate-500">Letzter Handshake:</span>
                          <span className="ml-2">{new Date(wgStatus.last_handshake).toLocaleString('de-DE')}</span>
                        </div>
                      )}
                      {wgStatus.transfer_rx !== null && (
                        <div>
                          <span className="text-slate-500">Transfer:</span>
                          <span className="ml-2">
                            RX: {Math.round((wgStatus.transfer_rx || 0) / 1024)} KB / TX: {Math.round((wgStatus.transfer_tx || 0) / 1024)} KB
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3">
                  {!wgStatus?.configured ? (
                    <button
                      onClick={generateWgKeys}
                      disabled={wgGenerating}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Key size={18} />
                      {wgGenerating ? 'Generiere...' : 'Schlüssel generieren'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={toggleWgEnabled}
                        disabled={wgEnabling || wgInstalling}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                          wgStatus?.enabled
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {wgStatus?.enabled ? <WifiOff size={18} /> : <Wifi size={18} />}
                        {wgEnabling ? 'Bitte warten...' : wgStatus?.enabled ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                      <button
                        onClick={generateWgKeys}
                        disabled={wgGenerating || wgInstalling}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Key size={18} />
                        {wgGenerating ? 'Generiere...' : 'Neu generieren'}
                      </button>
                      <button
                        onClick={installWireGuard}
                        disabled={wgInstalling || !wgStatus?.wg_ip || !wgStatus?.public_key || !form.ipv4}
                        title={!form.ipv4 ? 'IPv4-Adresse erforderlich' : !wgStatus?.wg_ip ? 'Erst Schlüssel generieren' : 'WireGuard auf Server installieren'}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download size={18} />
                        {wgInstalling ? 'Installiere...' : 'Installieren'}
                      </button>
                    </>
                  )}
                </div>

                {/* Terminal Output */}
                {wgTerminalOutput.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-slate-700">Installation</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        wgTerminalStatus === 'running' ? 'bg-blue-100 text-blue-700' :
                        wgTerminalStatus === 'success' ? 'bg-green-100 text-green-700' :
                        wgTerminalStatus === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {wgTerminalStatus === 'running' ? 'Läuft...' :
                         wgTerminalStatus === 'success' ? 'Erfolgreich' :
                         wgTerminalStatus === 'error' ? 'Fehler' : 'Bereit'}
                      </span>
                    </div>
                    <div
                      ref={wgTerminalRef}
                      className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono h-64 overflow-y-auto"
                    >
                      {wgTerminalOutput.map((line, i) => (
                        <div key={i} className={
                          line.startsWith('✓') ? 'text-green-400' :
                          line.startsWith('✗') ? 'text-red-400' :
                          line.startsWith('[') ? 'text-blue-400' :
                          ''
                        }>
                          {line || '\u00A0'}
                        </div>
                      ))}
                      {wgInstalling && (
                        <div className="inline-block w-2 h-4 bg-slate-100 animate-pulse ml-1"></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mumble-Bots Tab */}
        {activeTab === 'mumble-bots' && (
          <div className="p-6">
            {isNew ? (
              <div className="text-center text-slate-500 py-12">
                Speichern Sie den Server zuerst, um Mumble-Bots zu konfigurieren.
              </div>
            ) : (
              <div className="space-y-6 max-w-3xl">
                <p className="text-slate-500">Mumble-Bots Konfiguration kommt hier...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Terminal Modal */}
      {showTerminal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Terminal size={18} className="text-slate-400" />
                <span className="text-slate-200 font-medium">
                  Installation auf {form.host}
                </span>
                {terminalStatus === 'running' && (
                  <span className="flex items-center gap-2 px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded text-sm">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    Läuft...
                  </span>
                )}
                {terminalStatus === 'success' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-green-600/20 text-green-400 rounded text-sm">
                    <CheckCircle size={14} />
                    Erfolgreich
                  </span>
                )}
                {terminalStatus === 'error' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-red-600/20 text-red-400 rounded text-sm">
                    <XCircle size={14} />
                    Fehlgeschlagen
                  </span>
                )}
              </div>
              <button
                onClick={closeTerminal}
                className="text-slate-400 hover:text-slate-200 transition-colors p-1"
                title="Schließen"
              >
                <X size={20} />
              </button>
            </div>

            {/* Terminal Output */}
            <div
              ref={terminalRef}
              className="flex-1 overflow-y-auto p-4 font-mono text-sm text-slate-300 bg-slate-950"
              style={{ minHeight: '300px', maxHeight: '60vh' }}
            >
              {terminalOutput.length === 0 ? (
                <div className="text-slate-500">Warte auf Ausgabe...</div>
              ) : (
                terminalOutput.map((line, index) => (
                  <div
                    key={index}
                    className={`whitespace-pre-wrap ${
                      line.startsWith('>>>') ? 'text-blue-400' :
                      line.startsWith('✓') ? 'text-green-400' :
                      line.startsWith('✗') ? 'text-red-400' :
                      line.startsWith('[') && line.includes('/') ? 'text-yellow-400' :
                      'text-slate-300'
                    }`}
                  >
                    {line || '\u00A0'}
                  </div>
                ))
              )}
              {terminalStatus === 'running' && (
                <div className="text-slate-500 mt-2">
                  <span className="inline-block w-2 h-4 bg-slate-400 animate-pulse"></span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700 bg-slate-900">
              <div className="text-sm text-slate-500">
                {terminalOutput.length} Zeilen
              </div>
              <button
                onClick={closeTerminal}
                disabled={terminalStatus === 'running'}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  terminalStatus === 'running'
                    ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                }`}
              >
                {terminalStatus === 'running' ? 'Bitte warten...' : 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Result Modal */}
      {syncResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              {syncResult.failed_channels === 0 ? (
                <CheckCircle className="text-green-500" size={28} />
              ) : (
                <AlertCircle className="text-amber-500" size={28} />
              )}
              <h2 className="text-xl font-bold text-slate-800">
                Tenant-Synchronisierung
              </h2>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Tenants:</span>
                  <span className="ml-2 font-medium">{syncResult.tenants_synced}</span>
                </div>
                <div>
                  <span className="text-slate-500">Kanaele gesamt:</span>
                  <span className="ml-2 font-medium">{syncResult.total_channels}</span>
                </div>
                <div>
                  <span className="text-slate-500">Synchronisiert:</span>
                  <span className="ml-2 font-medium text-green-600">{syncResult.synced_channels}</span>
                </div>
                <div>
                  <span className="text-slate-500">Fehlgeschlagen:</span>
                  <span className={`ml-2 font-medium ${syncResult.failed_channels > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                    {syncResult.failed_channels}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Benutzer synchronisiert:</span>
                  <span className="ml-2 font-medium">{syncResult.total_users_synced}</span>
                </div>
                <div>
                  <span className="text-slate-500">AudioCasts synchronisiert:</span>
                  <span className="ml-2 font-medium">{syncResult.total_audiocast_synced}</span>
                </div>
              </div>
            </div>

            {syncResult.errors.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-red-600 mb-2">Fehler:</h3>
                <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {syncResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <p className="text-sm text-slate-600 mb-4">
              {syncResult.failed_channels === 0
                ? 'Alle Kanaele wurden erfolgreich synchronisiert.'
                : 'Einige Kanaele konnten nicht synchronisiert werden. Bitte pruefen Sie die ICE-Konfiguration.'}
            </p>

            <div className="flex justify-end">
              <button
                onClick={() => setSyncResult(null)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
